// ============================================================================
// Contracts — row card for the list view. Mirrors project-card.tsx visual rhythm.
// ============================================================================

import { Link } from 'react-router-dom';
import type { ContractRow } from './types';
import {
  BILLING_MODEL_CLASSES,
  CONTRACT_STATUS_CLASSES,
  CONTRACT_STATUS_LABELS,
  CONTRACT_TYPE_CLASSES,
  CONTRACT_TYPE_LABELS,
} from './types';

function fmtCurrency(n: number | null | undefined): string {
  if (n == null) return '—';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
function fmtDate(s: string | null | undefined): string {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleDateString();
  } catch {
    return s;
  }
}

function daysBetween(from: string, to: Date): number {
  const f = new Date(from);
  if (Number.isNaN(f.getTime())) return 0;
  return Math.floor((to.getTime() - f.getTime()) / (1000 * 60 * 60 * 24));
}

const PENDING_STATUSES = new Set([
  'Pending_Finance',
  'Pending_Legal',
  'Pending_Executive',
]);

interface ContractCardProps {
  row: ContractRow;
}

export function ContractCard({ row }: ContractCardProps) {
  const accountName = row.accounts?.account_name ?? '—';
  const facilityName = row.facilities?.facility_name ?? null;
  const creator = row.creator
    ? `${row.creator.employee_name_first ?? ''} ${row.creator.employee_name_last ?? ''}`.trim()
    : null;

  const daysInStatus = daysBetween(row.updated_at, new Date());
  const stale = PENDING_STATUSES.has(row.status) && daysInStatus > 5;

  return (
    <li>
      <Link
        to={`/contracts/${row.contract_id}`}
        className="block rounded-md border border-tcs-border bg-tcs-bg p-3 transition hover:border-tcs-text/40"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-tcs-text-muted">
                {row.contract_number ?? row.contract_id}
              </span>
              <span
                className={`inline-flex items-center rounded-md px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide ${
                  CONTRACT_STATUS_CLASSES[row.status] ??
                  'bg-tcs-surface text-tcs-text-secondary'
                }`}
              >
                {CONTRACT_STATUS_LABELS[row.status] ?? row.status}
              </span>
              <span
                className={`inline-flex items-center rounded-md px-2 py-[2px] text-[10px] font-medium uppercase tracking-wide ${
                  CONTRACT_TYPE_CLASSES[row.contract_type] ??
                  'bg-tcs-bg-muted text-tcs-text-secondary'
                }`}
              >
                {CONTRACT_TYPE_LABELS[row.contract_type] ?? row.contract_type}
              </span>
              <span
                className={`inline-flex items-center rounded-md px-2 py-[2px] text-[10px] font-medium uppercase tracking-wide ${
                  BILLING_MODEL_CLASSES[row.billing_model] ??
                  'bg-tcs-surface text-tcs-text-secondary'
                }`}
              >
                {row.billing_model}
              </span>
              {stale && (
                <span className="inline-flex items-center rounded-md bg-st-danger-bg px-2 py-[2px] text-[10px] font-semibold uppercase tracking-wide text-st-danger-fg">
                  {daysInStatus}d stale
                </span>
              )}
            </div>
            <div className="mt-1 truncate text-sm font-medium text-tcs-text">
              {accountName}
              {facilityName ? (
                <span className="text-tcs-text-secondary">
                  {' '}
                  · {facilityName}
                </span>
              ) : (
                <span className="text-tcs-text-secondary">
                  {' '}
                  ·{' '}
                  {row.covered_facility_count != null &&
                  row.covered_facility_count > 0
                    ? `${row.covered_facility_count} ${
                        row.covered_facility_count === 1
                          ? 'facility'
                          : 'facilities'
                      }`
                    : 'Account-wide'}
                </span>
              )}
            </div>
            {row.scope_summary && (
              <div className="mt-1 line-clamp-1 text-xs text-tcs-text-secondary">
                {row.scope_summary}
              </div>
            )}
          </div>
          <div className="text-right text-xs text-tcs-text-secondary">
            {row.billing_model === 'Hourly' || row.billing_model === 'Project_Only' ? (
              <div className="font-mono text-sm text-tcs-text">
                {row.hourly_rate != null
                  ? `${fmtCurrency(row.hourly_rate)}/hr`
                  : '—'}
              </div>
            ) : (
              <div className="font-mono text-sm text-tcs-text">
                {row.monthly_rate != null
                  ? `${fmtCurrency(row.monthly_rate)}/mo`
                  : '—'}
              </div>
            )}
            <div className="mt-0.5 text-[11px]">
              {creator ? `by ${creator}` : ''}
            </div>
            <div className="text-[11px] text-tcs-text-muted">
              {fmtDate(row.created_at)}
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}
